import { useEffect, useRef } from 'react'
import { Info, X } from 'lucide-react'
import type { HelpSeat, HelpTopic } from './types.js'

interface HelpPanelProps {
  topic: HelpTopic
  seat: HelpSeat
  /** Looks up a related topic for the reader's seat; null hides the link. */
  topicById: (id: string) => HelpTopic | null
  onOpenTopic: (topic: HelpTopic) => void
  onClose: () => void
}

/** The guide a help icon opens: a panel at the side (the whole width on a phone) that leaves the screen it explains in view. */
export function HelpPanel({ topic, seat, topicById, onOpenTopic, onClose }: HelpPanelProps) {
  const heading = useRef<HTMLHeadingElement>(null)
  useEffect(() => {
    heading.current?.focus()
    function closeOnEscape(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [topic.id, onClose])

  const sections = topic.sections.filter((section) => !section.seats || section.seats.includes(seat))
  const related = (topic.related ?? []).map(topicById).filter((entry): entry is HelpTopic => entry !== null)

  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-ink/20" onClick={onClose}>
      <aside role="dialog" aria-modal="true" aria-label={`Help: ${topic.title}`} className="h-full w-full max-w-sm overflow-y-auto bg-white shadow-xl border-l border-marine/15" onClick={(event) => event.stopPropagation()}>
        <div className="sticky top-0 flex items-start justify-between gap-3 border-b border-marine/15 bg-white px-5 py-4">
          <div>
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-marine">
              <Info className="h-3.5 w-3.5" aria-hidden="true" />
              About this screen
            </div>
            <h2 ref={heading} tabIndex={-1} className="mt-1 text-base font-black text-ink focus:outline-none">
              {topic.title}
            </h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close help" title="Close" className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-ink-mid hover:bg-marine/10 hover:text-ink">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex flex-col gap-4 px-5 py-4 text-[13px] leading-relaxed text-ink">
          <p className="text-ink">{topic.summary}</p>
          {sections.map((section) => (
            <section key={section.heading}>
              <h3 className="text-[11px] font-bold uppercase tracking-wide text-marine">{section.heading}</h3>
              <ul className="mt-1.5 flex list-disc flex-col gap-1 pl-4 text-ink-mid marker:text-marine/50">
                {section.points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </section>
          ))}
          {related.length > 0 && (
            <section>
              <h3 className="text-[11px] font-bold uppercase tracking-wide text-marine">Related</h3>
              <ul className="mt-1.5 flex flex-col gap-1">
                {related.map((entry) => (
                  <li key={entry.id}>
                    <button type="button" onClick={() => onOpenTopic(entry)} className="text-left font-bold text-marine hover:text-marine-dk">
                      {entry.title}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {topic.learnMore && <p className="border-t border-marine/10 pt-3 text-[11px] text-ink-mid">Learn more: {topic.learnMore}</p>}
        </div>
      </aside>
    </div>
  )
}
